const DEFAULT_ICONS = ['★', '★', '★', '★', '★'];
const DEFAULT_COOLDOWN_MS = 2 * 60 * 60 * 1000;
const VALID_TYPES = new Set(['post', 'story', 'joke']);

class RatingWidget extends HTMLElement {
  connectedCallback() {
    this.type = this.getAttribute('type') || 'post';
    this.itemId = this.getAttribute('item-id') || '';
    const configuredCooldown = Number(this.getAttribute('cooldown-ms'));
    this.cooldownMs = Number.isFinite(configuredCooldown) && configuredCooldown > 0
      ? configuredCooldown
      : DEFAULT_COOLDOWN_MS;
    this.apiUrl = this.getAttribute('api-url') || '/api.php';
    this.icons = this.parseIcons();
    this.storageKey = `lachvegas:rating:${this.type}:${this.itemId}`;
    this.voteInProgress = false;
    this.render();

    if (!VALID_TYPES.has(this.type) || !/^[a-zA-Z0-9_-]{1,120}$/.test(this.itemId)) {
      this.setStatus('Die Bewertung ist momentan nicht verfügbar.');
      this.disableButtons();
      return;
    }

    this.loadVoteState();
    this.loadSummary();
  }

  parseIcons() {
    const icons = (this.getAttribute('icons') || '').split('|');
    return icons.length === 5 && icons.every(icon => icon.trim())
      ? icons.map(icon => icon.trim())
      : DEFAULT_ICONS;
  }

  render() {
    this.innerHTML = `
            <section class="rating-widget__inner" aria-label="Bewertung">
                <h2 class="rating-widget__title">Wie findest du diesen Inhalt?</h2>
                <div class="rating-widget__choices" role="group" aria-label="Bewertung von 1 bis 5">
                    <button class="rating-widget__choice" type="button" data-value="1" aria-label="1 von 5: sehr schlecht" aria-pressed="false"></button>
                    <button class="rating-widget__choice" type="button" data-value="2" aria-label="2 von 5: nicht gut" aria-pressed="false"></button>
                    <button class="rating-widget__choice" type="button" data-value="3" aria-label="3 von 5: mittelmäßig" aria-pressed="false"></button>
                    <button class="rating-widget__choice" type="button" data-value="4" aria-label="4 von 5: gut" aria-pressed="false"></button>
                    <button class="rating-widget__choice" type="button" data-value="5" aria-label="5 von 5: super" aria-pressed="false"></button>
                </div>
                <p class="rating-widget__summary" data-rating-summary aria-live="polite">Bewertungen werden geladen …</p>
                <p class="rating-widget__status" data-rating-status aria-live="polite"></p>
            </section>
        `;

    this.summaryNode = this.querySelector('[data-rating-summary]');
    this.statusNode = this.querySelector('[data-rating-status]');
    this.buttons = [...this.querySelectorAll('[data-value]')];
    this.buttons.forEach((button, index) => {
      button.textContent = this.icons[index];
      button.addEventListener('click', () => this.submitRating(Number(button.dataset.value)));
      button.addEventListener('pointerenter', () => this.previewRating(Number(button.dataset.value)));
      button.addEventListener('pointerleave', () => this.previewRating(0));
      button.addEventListener('focus', () => this.previewRating(Number(button.dataset.value)));
      button.addEventListener('blur', () => this.previewRating(0));
    });
  }

  loadVoteState() {
    try {
      const savedVote = localStorage.getItem(this.storageKey);
      if (!savedVote) {
        return;
      }

      const { expiresAt, rating } = JSON.parse(savedVote);
      if (Number(expiresAt) > Date.now()) {
        this.setStatus('Du hast diesen Inhalt bereits bewertet. Danke!');
        this.setButtonsDisabled(true);
        this.markRating(Number(rating));
        return;
      }

      localStorage.removeItem(this.storageKey);
    } catch {
      this.setStatus('Die Abstimmungssperre kann in diesem Browser nicht gespeichert werden.');
    }
  }

  async loadSummary() {
    try {
      const params = new URLSearchParams({
        action: 'get_rating',
        type: this.type,
        id: this.itemId,
      });
      const response = await fetch(`${this.apiUrl}?${params}`, { cache: 'no-store' });
      const data = await response.json();
      if (!response.ok) {
        throw new Error(data.error || 'Bewertungen konnten nicht geladen werden.');
      }
      this.updateSummary(data);
    } catch (error) {
      this.setStatus(error.message || 'Bewertungen konnten nicht geladen werden.');
    }
  }

  async submitRating(rating) {
    if (this.voteInProgress || this.buttons[0].disabled) {
      return;
    }

    if (!Number.isInteger(rating) || rating < 1 || rating > 5) {
      return;
    }

    const expiresAt = Date.now() + this.cooldownMs;
    const savedVote = JSON.stringify({ expiresAt, rating });
    try {
      localStorage.setItem(this.storageKey, savedVote);
    } catch {
      this.setStatus('Deine Bewertung wurde nicht gesendet, da die Abstimmungssperre nicht gespeichert werden kann.');
      return;
    }

    this.voteInProgress = true;
    this.setButtonsDisabled(true);
    this.setStatus('Deine Bewertung wird gespeichert …');

    try {
      const response = await fetch(`${this.apiUrl}?action=save_rating`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
        body: JSON.stringify({ type: this.type, id: this.itemId, rating }),
      });
      const data = await response.json();
      if (!response.ok) {
        throw new Error(data.error || 'Deine Bewertung konnte nicht gespeichert werden.');
      }

      this.updateSummary(data);
      this.markRating(rating);
      this.setStatus('Danke für deine Bewertung!');
    } catch (error) {
      this.setStatus(`${error.message || 'Deine Bewertung konnte nicht gespeichert werden.'} Die Abstimmungssperre bleibt vorsichtshalber bestehen.`);
    } finally {
      this.voteInProgress = false;
    }
  }

  updateSummary(data) {
    const count = Number(data.count) || 0;
    const average = Number(data.average) || 0;
    this.summaryNode.textContent = count
      ? `Durchschnitt: ${average.toLocaleString('de-DE', { minimumFractionDigits: 1, maximumFractionDigits: 2 })} von 5 (${count} ${count === 1 ? 'Bewertung' : 'Bewertungen'})`
      : 'Noch keine Bewertungen';
  }

  previewRating(rating) {
    if (this.buttons[0].disabled) {
      return;
    }
    this.buttons.forEach((button, index) => {
      button.classList.toggle('is-preview', rating > 0 && index < rating);
    });
  }

  markRating(rating) {
    this.buttons.forEach((button, index) => {
      const selected = index < rating;
      button.classList.toggle('is-selected', selected);
      button.setAttribute('aria-pressed', String(index + 1 === rating));
    });
  }

  setButtonsDisabled(disabled) {
    this.buttons.forEach(button => {
      button.disabled = disabled;
    });
  }

  disableButtons() {
    this.setButtonsDisabled(true);
  }

  setStatus(message) {
    this.statusNode.textContent = message;
  }
}

if (!customElements.get('rating-widget')) {
  customElements.define('rating-widget', RatingWidget);
}
